import { withScreenshotDensity } from 'BomdisoftComponents/docs-shell/density';
import { withDocsHeadingIds } from 'BomdisoftComponents/docs-shell/headings';
import { withLazyImages } from 'BomdisoftComponents/docs-shell/lazy-images';
import type { DocsThemeOverrides } from 'BomdisoftComponents/docs-shell/types';
import { attributes, Html, html } from 'BomdisoftComponents/html/index';
import { type Block, type EditorData, pageStylesheet, renderBlocks, tokensToLightDarkBlock } from '@clepit/core';

const INTERACTIVE_TYPES = new Set<Block['type']>(['code', 'openapi', 'table']);

const withStableIds = (blocks: readonly Block[], prefix: string): Block[] =>
  blocks.map((block, index) => {
    const path = `${prefix}-${index}`;
    let renumbered: Block = block;
    if (block.type === 'collapsible') renumbered = { ...block, data: { ...block.data, children: withStableIds(block.data.children ?? [], path) } };
    if (block.type === 'columns') renumbered = { ...block, data: { ...block.data, columns: block.data.columns.map((column, position) => withStableIds(column, `${path}-${position}`)) } };
    return renumbered.id ? renumbered : { ...renumbered, id: `clepit-anon-${path}` };
  });

const hasInteractiveContent = (block: Block): boolean => {
  if (INTERACTIVE_TYPES.has(block.type)) return true;
  if (block.type === 'collapsible') return (block.data.children ?? []).some(hasInteractiveContent);
  if (block.type === 'columns') return block.data.columns.some(column => column.some(hasInteractiveContent));
  return false;
};

const toActivationData = (block: Block): Block => {
  if (block.type === 'openapi') return { ...block, data: {} };
  if (block.type === 'collapsible') return { ...block, data: { ...block.data, children: (block.data.children ?? []).map(toActivationData) } };
  if (block.type === 'columns') return { ...block, data: { ...block.data, columns: block.data.columns.map(column => column.map(toActivationData)) } };
  return block;
};

const fnv1a = (text: string): string => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index++) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
};

const inClepitLayer = (css: string): string => {
  const registrations: string[] = [];
  const rest = css.replace(/@property\s+[\w-]+\s*\{[^{}]*\}/g, rule => {
    registrations.push(rule);
    return '';
  });
  return `${registrations.join('\n')}\n@layer clepit {\n${rest}\n}`;
};

export const docsContentStylesheet = () => inClepitLayer(pageStylesheet());

export type DocsContentOptions = { content: EditorData; testId?: string; themeOverrides?: DocsThemeOverrides };

export const renderDocsContent = ({ content, testId, themeOverrides }: DocsContentOptions) => {
  const blocks = withStableIds(Array.isArray(content.blocks) ? content.blocks : [], 'cb');
  const markup = withLazyImages(withScreenshotDensity(withDocsHeadingIds(renderBlocks(blocks), content)));
  const overrides = themeOverrides ? tokensToLightDarkBlock(themeOverrides.light ?? {}, themeOverrides.dark ?? {}, '&') : '';
  const overridesClass = overrides ? `clepit-ov-${fnv1a(overrides)}` : undefined;
  const overridesStyle = overridesClass ? html`<style>${new Html(inClepitLayer(tokensToLightDarkBlock(themeOverrides?.light ?? {}, themeOverrides?.dark ?? {}, `.${overridesClass}`)))}</style>` : html``;
  const interactive = blocks.filter(hasInteractiveContent).map(toActivationData);
  const data = interactive.length > 0 ? html`<script type="application/json" data-clepit-blocks>${new Html(JSON.stringify(interactive).replace(/</g, '\\u003c'))}</script>` : html``;
  return html`${overridesStyle}<div${attributes({ class: ['clepit-renderer-content', overridesClass].filter(Boolean).join(' '), 'data-clepit-theme': 'auto', 'data-testid': testId })}>${new Html(markup)}</div>${data}`;
};

type BlockActivation = { activateBlocks: (root: HTMLElement, blocks: Block[]) => () => void };

export const delegateDocsContent = (root: ParentNode, load: () => Promise<BlockActivation> = () => import('@clepit/core')) => {
  const content = root.querySelector<HTMLElement>('.clepit-renderer-content');
  const data = root.querySelector('script[data-clepit-blocks]');
  if (content === null || data === null) return () => {};
  const blocks = JSON.parse(data.textContent ?? '[]') as Block[];
  let released = false;
  let release = () => {};
  void load().then(({ activateBlocks }) => {
    if (!released) release = activateBlocks(content, blocks);
  });
  return () => {
    released = true;
    release();
  };
};
